import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  FileText,
  Mail,
  Receipt,
  Search,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

interface PreviewItem {
  financialEntryId: string;
  referencia: string;
  valor: string;
  dataVencimento: string;
  numeroParcela: number | null;
  totalParcelas: number | null;
  status: 'PENDING' | 'OVERDUE';
  cliente: { id: string; razaoSocial: string; cpfCnpj: string; email: string | null } | null;
  contrato: { id: string; codigoContrato: string } | null;
  empresaEmissora: { id: string; razaoSocial: string } | null;
  jaTemInvoice: boolean;
  jaTemBoleto: boolean;
}

interface IssuerCompanyMini {
  id: string;
  razaoSocial: string;
  cnpj: string;
  isDefault: boolean;
}

interface RunResultItem {
  financialEntryId: string;
  nfse?: { status: 'queued' | 'failed' | 'skipped'; error?: string; invoiceId?: string };
  boleto?: { status: 'created' | 'failed' | 'skipped'; error?: string; chargeId?: string };
  email?: { status: 'sent' | 'failed' | 'skipped'; error?: string };
}

interface RunResponse {
  summary: {
    total: number;
    nfseQueued: number;
    nfseFailed: number;
    boletosCreated: number;
    boletosFailed: number;
    emailsSent: number;
  };
  results: RunResultItem[];
}

function firstDayOfMonth(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function lastDayOfMonth(d = new Date()): string {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
}

export function BatchBillingPage() {
  const [dateStart, setDateStart] = useState(firstDayOfMonth());
  const [dateEnd, setDateEnd] = useState(lastDayOfMonth());
  const [issuerCompanyId, setIssuerCompanyId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Opcoes do lote
  const [emitirNfse, setEmitirNfse] = useState(true);
  const [gerarBoleto, setGerarBoleto] = useState(true);
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [aliquotaIss, setAliquotaIss] = useState('3.00');
  const [servicoCodigo, setServicoCodigo] = useState('010801');

  const [runResult, setRunResult] = useState<RunResponse | null>(null);

  const { data: companies } = useQuery({
    queryKey: ['issuer-companies'],
    queryFn: () => apiFetch<IssuerCompanyMini[]>('/issuer-companies'),
  });

  const { data: preview, isLoading, refetch } = useQuery({
    queryKey: ['batch-billing-preview', dateStart, dateEnd, issuerCompanyId],
    queryFn: () => {
      const params = new URLSearchParams({ dateStart, dateEnd });
      if (issuerCompanyId) params.set('issuerCompanyId', issuerCompanyId);
      return apiFetch<PreviewItem[]>(`/batch-billing/preview?${params}`);
    },
    enabled: !!dateStart && !!dateEnd,
  });

  const filtered = useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    if (!q) return preview;
    return preview.filter((p) =>
      [
        p.cliente?.razaoSocial,
        p.cliente?.cpfCnpj,
        p.contrato?.codigoContrato,
        p.referencia,
      ]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q)),
    );
  }, [preview, search]);

  const totalSelected = useMemo(
    () =>
      filtered
        .filter((p) => selected.has(p.financialEntryId))
        .reduce((sum, p) => sum + Number(p.valor), 0),
    [filtered, selected],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(checked: boolean) {
    if (checked) {
      setSelected(new Set(filtered.map((p) => p.financialEntryId)));
    } else {
      setSelected(new Set());
    }
  }

  const run = useMutation({
    mutationFn: () =>
      apiFetch<RunResponse>('/batch-billing/run', {
        method: 'POST',
        body: JSON.stringify({
          financialEntryIds: Array.from(selected),
          emitirNfse,
          gerarBoleto,
          enviarEmail,
          servicoCodigo,
          aliquotaIss: Number(aliquotaIss),
        }),
      }),
    onSuccess: (r) => {
      setRunResult(r);
      setSelected(new Set());
      void refetch();
    },
  });

  const canRun = selected.size > 0 && (emitirNfse || gerarBoleto);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Faturar em Lote</h1>
          <p className="text-sm text-muted-foreground">
            Selecione os lançamentos do mês e emita NFSe + boleto + e-mail em uma única ação.
          </p>
        </div>
      </header>

      {/* Filtros */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Data inicial</span>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Data final</span>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Empresa emissora</span>
            <select
              value={issuerCompanyId}
              onChange={(e) => setIssuerCompanyId(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[240px]"
            >
              <option value="">— Todas —</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial} {c.isDefault ? '(padrão)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex-1 space-y-1 min-w-[220px]">
            <span className="text-xs font-medium text-muted-foreground">Buscar</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder="cliente, contrato, CNPJ…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm"
              />
            </div>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Atalhos:</span>
          <button
            type="button"
            onClick={() => {
              setDateStart(firstDayOfMonth());
              setDateEnd(lastDayOfMonth());
            }}
            className="rounded-md border border-input px-2 py-1 hover:bg-accent"
          >
            Mês atual
          </button>
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              d.setMonth(d.getMonth() - 1);
              setDateStart(firstDayOfMonth(d));
              setDateEnd(lastDayOfMonth(d));
            }}
            className="rounded-md border border-input px-2 py-1 hover:bg-accent"
          >
            Mês anterior
          </button>
          <button
            type="button"
            onClick={() => {
              const today = new Date().toISOString().slice(0, 10);
              setDateStart(today);
              setDateEnd(today);
            }}
            className="rounded-md border border-input px-2 py-1 hover:bg-accent"
          >
            Hoje
          </button>
        </div>
      </section>

      {/* Tabela */}
      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {preview && filtered.length === 0 && (
          <p className="p-12 text-center text-sm text-muted-foreground">
            Nenhum lançamento pendente nessa competência.
          </p>
        )}
        {filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={filtered.every((p) => selected.has(p.financialEntryId))}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Empresa</th>
                <th className="px-3 py-2 font-medium">Referência</th>
                <th className="px-3 py-2 font-medium">Venc.</th>
                <th className="px-3 py-2 font-medium text-right">Valor</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Faturamento</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.financialEntryId}
                  className="border-b border-border last:border-0 hover:bg-accent/10"
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(p.financialEntryId)}
                      onChange={() => toggle(p.financialEntryId)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{p.cliente?.razaoSocial ?? '—'}</div>
                    {p.cliente?.email && (
                      <div className="text-[10px] text-muted-foreground truncate max-w-xs">
                        {p.cliente.email}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {p.empresaEmissora ? (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
                        {p.empresaEmissora.razaoSocial}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">padrão</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs">{p.referencia}</div>
                    {p.numeroParcela && p.totalParcelas && (
                      <div className="text-[10px] text-muted-foreground">
                        Parcela {p.numeroParcela}/{p.totalParcelas}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {new Date(p.dataVencimento + 'T00:00:00Z').toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatBRL(Number(p.valor))}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                        p.status === 'OVERDUE'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {p.status === 'OVERDUE' ? 'Em atraso' : 'A vencer'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[10px]">
                    {p.jaTemInvoice && (
                      <span className="mr-1 inline-block rounded bg-primary/10 px-1 py-0.5 text-primary">
                        NFSe ✓
                      </span>
                    )}
                    {p.jaTemBoleto && (
                      <span className="inline-block rounded bg-success/10 px-1 py-0.5 text-success">
                        Boleto ✓
                      </span>
                    )}
                    {!p.jaTemInvoice && !p.jaTemBoleto && (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Painel de ação */}
      {filtered.length > 0 && (
        <section className="sticky bottom-4 rounded-lg border-2 border-primary/30 bg-card p-4 shadow-lg">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-sm">
                <strong>{selected.size}</strong> de {filtered.length} selecionados ·{' '}
                <strong>{formatBRL(totalSelected)}</strong> a faturar
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={emitirNfse}
                  onChange={(e) => setEmitirNfse(e.target.checked)}
                />
                <FileText className="h-4 w-4" />
                Emitir NFSe
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={gerarBoleto}
                  onChange={(e) => setGerarBoleto(e.target.checked)}
                />
                <Receipt className="h-4 w-4" />
                Gerar boleto
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={enviarEmail}
                  onChange={(e) => setEnviarEmail(e.target.checked)}
                />
                <Mail className="h-4 w-4" />
                Enviar e-mail
              </label>
            </div>
          </div>

          {emitirNfse && (
            <div className="mt-3 grid grid-cols-2 gap-3 md:max-w-md">
              <label className="space-y-1">
                <span className="text-[10px] uppercase text-muted-foreground">
                  Código serviço (cTribNac)
                </span>
                <input
                  className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
                  value={servicoCodigo}
                  onChange={(e) => setServicoCodigo(e.target.value)}
                  maxLength={6}
                />
              </label>
              <label className="space-y-1">
                <span className="text-[10px] uppercase text-muted-foreground">Alíquota ISS (%)</span>
                <input
                  className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
                  value={aliquotaIss}
                  onChange={(e) => setAliquotaIss(e.target.value)}
                />
              </label>
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => run.mutate()}
              disabled={!canRun || run.isPending}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Zap className="h-4 w-4" />
              {run.isPending ? 'Faturando…' : 'Faturar selecionados'}
            </button>
          </div>
        </section>
      )}

      {/* Resultado */}
      {runResult && (
        <section className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            Lote processado
          </h2>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs md:grid-cols-5">
            <Stat label="Total" value={runResult.summary.total} />
            <Stat label="NFSe na fila" value={runResult.summary.nfseQueued} positive />
            <Stat label="NFSe falhou" value={runResult.summary.nfseFailed} negative />
            <Stat label="Boletos criados" value={runResult.summary.boletosCreated} positive />
            <Stat label="E-mails enviados" value={runResult.summary.emailsSent} positive />
          </div>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer font-medium">Detalhes por item</summary>
            <table className="mt-2 w-full">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left">Lançamento</th>
                  <th className="text-left">NFSe</th>
                  <th className="text-left">Boleto</th>
                  <th className="text-left">E-mail</th>
                </tr>
              </thead>
              <tbody>
                {runResult.results.map((r) => (
                  <tr key={r.financialEntryId} className="border-t border-border/50">
                    <td className="font-mono text-[10px]">{r.financialEntryId.slice(0, 8)}</td>
                    <td>{statusBadge(r.nfse?.status, r.nfse?.error)}</td>
                    <td>{statusBadge(r.boleto?.status, r.boleto?.error)}</td>
                    <td>{statusBadge(r.email?.status, r.email?.error)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  positive,
  negative,
}: {
  label: string;
  value: number;
  positive?: boolean;
  negative?: boolean;
}) {
  return (
    <div className="rounded-md bg-card p-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div
        className={`text-lg font-bold ${
          positive ? 'text-emerald-600' : negative && value > 0 ? 'text-destructive' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function statusBadge(
  status: string | undefined,
  error?: string,
): React.ReactNode {
  if (!status || status === 'skipped') return <span className="text-muted-foreground">—</span>;
  if (status === 'queued' || status === 'created' || status === 'sent') {
    return <span className="text-emerald-600">✓ {status}</span>;
  }
  return (
    <span className="text-destructive" data-dica={error}>
      ✗ {status}
    </span>
  );
}
